import * as XLSX from 'xlsx';
import { StagedRow, onlyDigits } from './prospeccaoLista';
import { LemitTelefone, LemitEmail, LemitSocio } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────
// Exporta leads da Lista Fria no modelo de IMPORTAÇÃO do CRM do CNPJ Biz
// (contexto/bruno (crm)/crm-import-modelv5 (1).xlsx).
//
// Fecha o ciclo pedido pelo Bruno: CNPJ Biz traz o básico → a Lemit enriquece
// empresa e sócios → volta pro CNPJ Biz com telefone/e-mail do dono.
//
// Contrato do modelo (aba "DICAS" + "Tutorial"):
//   • A aba PRECISA se chamar "Leads" — o importador procura por esse nome.
//   • 59 colunas, nesta ordem exata.
//   • Até 5 contatos por lead (contact_1..contact_5) — usamos os sócios.
//   • phone_type aceita 'cellphone' | 'phone'.
//   • organization_opening_at no formato YYYY-MM-DD.
// ─────────────────────────────────────────────────────────────────────────────

const COLUNAS = [
  'title', 'responsible_email', 'description', 'thermometer', 'value', 'tags',
  'organization_title', 'organization_document_number', 'organization_company_name',
  'organization_fantasy_name', 'organization_opening_at', 'organization_company_size',
  'organization_category', 'organization_email_1', 'organization_email_2',
  'organization_phone_number_1', 'organization_phone_type_1',
  'organization_phone_number_2', 'organization_phone_type_2',
  ...[1, 2, 3, 4, 5].flatMap((i) => [
    `contact_${i}_name`, `contact_${i}_role`, `contact_${i}_email_1`, `contact_${i}_email_2`,
    `contact_${i}_phone_number_1`, `contact_${i}_phone_type_1`,
    `contact_${i}_phone_number_2`, `contact_${i}_phone_type_2`,
  ]),
] as const;

// Receita/CNPJ Biz/Lemit devolvem tudo em CAIXA ALTA ("ALAOR DA SILVA RIBEIRO").
// No CRM do CNPJ Biz o nome fica gritando, então normalizamos para caixa normal.
const CONECTIVOS = new Set(['da', 'das', 'de', 'do', 'dos', 'e', 'di', 'du', 'del', 'della', 'la', 'le', 'van', 'von', 'y']);
/** Siglas societárias e afins que devem continuar maiúsculas. */
const SIGLAS = new Set(['LTDA', 'ME', 'EPP', 'EIRELI', 'MEI', 'SA', 'S/A', 'S.A', 'S.A.', 'CIA', 'ONG', 'CNPJ', 'CPF', 'II', 'III', 'IV', 'VI', 'VII', 'VIII', 'IX', 'XI', 'XII']);

/**
 * "ALAOR DA SILVA RIBEIRO" → "Alaor da Silva Ribeiro".
 * Só age quando o texto está todo em caixa alta — nomes já digitados com
 * capitalização própria (ex.: "iFood") passam intactos.
 */
function nomeProprio(v?: string | null): string {
  const s = String(v ?? '').trim();
  if (!s || /[a-zà-ÿ]/.test(s)) return s;
  return s
    .split(/\s+/)
    .map((tok, i) => {
      if (SIGLAS.has(tok)) return tok;
      if (/\d/.test(tok)) return tok;                       // 12345678900, 2ª, etc.
      const baixo = tok.toLowerCase();
      if (i > 0 && CONECTIVOS.has(baixo)) return baixo;
      // capitaliza também depois de hífen e apóstrofo ("SANT'ANA", "MARIA-JOSÉ")
      return baixo.replace(/(^|[-'’])([a-zà-ÿ])/g, (_m, sep, c) => sep + c.toUpperCase());
    })
    .join(' ');
}

/** +55DDNNNNNNNNN — formato do 1º exemplo do modelo (o importador também aceita mascarado). */
function fmtTelefone(digits?: string | null): string {
  const d = onlyDigits(digits);
  if (d.length < 10) return '';
  return `+55${d}`;
}

/** 9 dígitos (ou marcado como WhatsApp) = celular; 8 = fixo. */
function tipoTelefone(digits?: string | null, whatsapp?: boolean): string {
  const d = onlyDigits(digits);
  if (!d) return '';
  if (whatsapp) return 'cellphone';
  return d.length >= 11 ? 'cellphone' : 'phone';
}

const telDigits = (t: LemitTelefone): string => `${t.ddd ?? ''}${(t.numero ?? '').replace(/\D/g, '')}`.replace(/\D/g, '');

/** Ordena por ranking e devolve os dois primeiros telefones formatados + tipos. */
function doisTelefones(tels: LemitTelefone[], fallback?: string | null) {
  const ord = (tels || []).filter((t) => t?.numero).slice().sort((a, b) => (a.ranking ?? 99) - (b.ranking ?? 99));
  const lista = ord.map((t) => ({ num: telDigits(t), wa: !!t.whatsapp }));
  if (lista.length === 0 && fallback) lista.push({ num: onlyDigits(fallback), wa: false });
  return {
    n1: fmtTelefone(lista[0]?.num), t1: tipoTelefone(lista[0]?.num, lista[0]?.wa),
    n2: fmtTelefone(lista[1]?.num), t2: tipoTelefone(lista[1]?.num, lista[1]?.wa),
  };
}

function doisEmails(emails: LemitEmail[], fallback?: string | null) {
  const ord = (emails || []).filter((e) => e?.email).slice().sort((a, b) => (a.ranking ?? 99) - (b.ranking ?? 99));
  const lista = ord.map((e) => e.email as string);
  if (lista.length === 0 && fallback) lista.push(fallback);
  return { e1: lista[0] || '', e2: lista[1] || '' };
}

/** ISO/《Date》 → YYYY-MM-DD (o modelo pede esse formato). */
function fmtData(v: unknown): string {
  const s = String(v ?? '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
}

export interface ExportCnpjBizOpts {
  /** E-mail do dono do lead NA PLATAFORMA do CNPJ Biz (coluna obrigatória). */
  responsibleEmail: string;
  /** Vai para a coluna `tags` — ajuda a rastrear o lote no CNPJ Biz. */
  tag?: string;
  thermometer?: 1 | 2 | 3 | 4 | 5;
}

/** Monta a linha do modelo a partir de uma empresa da Lista Fria já enriquecida. */
function linhaDoLead(row: StagedRow, opts: ExportCnpjBizOpts): Record<string, string> {
  const enr = row.enriquecimento_data;
  const raw = row.raw_payload || {};
  // Colunas do CNPJ Biz que não têm campo canônico ficam em raw_payload.extras
  // (data_abertura, porte_empresa, atividades_principal…) — chaves normalizadas.
  const ex: Record<string, string> = raw.extras || {};
  const doc = onlyDigits(row.documento || row.cnpj);

  const empTels = enr?.empresa?.telefones || [];
  const empMails = enr?.empresa?.emails || [];
  const tel = doisTelefones(empTels, row.telefone);
  const mail = doisEmails(empMails, row.email);

  // Contatos = quadro societário. O sócio já enriquecido (dono) vem primeiro,
  // com telefone/e-mail; os demais entram só com o nome.
  const enriquecidos = enr?.socios_enriquecidos || [];
  const principalCpf = onlyDigits(enr?.socio_principal?.cpf);
  const socios: LemitSocio[] = enr?.socios || [];
  const ordenados = socios.slice().sort((a, b) => {
    const aP = onlyDigits(a.cpf) === principalCpf ? 1 : 0;
    const bP = onlyDigits(b.cpf) === principalCpf ? 1 : 0;
    if (aP !== bP) return bP - aP;                       // principal primeiro
    return (b.participacao ?? 0) - (a.participacao ?? 0);
  });

  const linha: Record<string, string> = {
    title: nomeProprio(row.nome || row.razao_social || doc),
    responsible_email: opts.responsibleEmail,
    description: `Lead da Lista Fria VigorHub · enriquecido pela Lemit${row.cidade ? ` · ${nomeProprio(row.cidade)}${row.estado ? '/' + row.estado : ''}` : ''}`,
    thermometer: String(opts.thermometer ?? 3),
    value: '',
    tags: opts.tag || 'VigorHub',
    organization_title: nomeProprio(row.razao_social || row.nome || doc),
    organization_document_number: doc,
    organization_company_name: nomeProprio(row.razao_social),
    organization_fantasy_name: nomeProprio(raw.nomeFantasia || row.nome),
    organization_opening_at: fmtData(ex.data_abertura),
    organization_company_size: ex.porte_empresa || '',
    organization_category: ex.atividades_principal || row.categoria || '',
    organization_email_1: mail.e1,
    organization_email_2: mail.e2,
    organization_phone_number_1: tel.n1,
    organization_phone_type_1: tel.t1,
    organization_phone_number_2: tel.n2,
    organization_phone_type_2: tel.t2,
  };

  ordenados.slice(0, 5).forEach((s, i) => {
    const n = i + 1;
    const cpf = onlyDigits(s.cpf);
    const enrS = enriquecidos.find((e) => onlyDigits(e.cpf) === cpf);
    const t = doisTelefones(enrS?.telefones || []);
    const e = doisEmails(enrS?.emails || []);
    const ehPrincipal = cpf && cpf === principalCpf;
    linha[`contact_${n}_name`] = nomeProprio(s.nome || enrS?.nome);
    linha[`contact_${n}_role`] = ehPrincipal
      ? `Representante legal${s.participacao != null ? ` (${s.participacao}%)` : ''}`
      : `Sócio${s.participacao != null ? ` (${s.participacao}%)` : ''}`;
    linha[`contact_${n}_email_1`] = e.e1;
    linha[`contact_${n}_email_2`] = e.e2;
    linha[`contact_${n}_phone_number_1`] = t.n1;
    linha[`contact_${n}_phone_type_1`] = t.t1;
    linha[`contact_${n}_phone_number_2`] = t.n2;
    linha[`contact_${n}_phone_type_2`] = t.t2;
  });

  COLUNAS.forEach((c) => { if (linha[c] === undefined) linha[c] = ''; });
  return linha;
}

export interface ExportResumo {
  total: number;
  comTelefoneEmpresa: number;
  comContatoSocio: number;
  arquivo: string;
}

/**
 * Gera e baixa o .xlsx no modelo do CNPJ Biz.
 * A aba se chama "Leads" (exigência do importador) e a ordem das colunas é a do modelo.
 */
export function exportarParaCnpjBiz(rows: StagedRow[], opts: ExportCnpjBizOpts): ExportResumo {
  const linhas = rows.map((r) => linhaDoLead(r, opts));

  // aoa_to_sheet preserva a ordem exata das colunas (json_to_sheet reordena por chave)
  const matriz = [
    COLUNAS as unknown as string[],
    ...linhas.map((l) => (COLUNAS as unknown as string[]).map((c) => l[c] ?? '')),
  ];
  const ws = XLSX.utils.aoa_to_sheet(matriz);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Leads');   // nome obrigatório

  const arquivo = `cnpjbiz_import_vigorhub_${new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(wb, arquivo);

  return {
    total: linhas.length,
    comTelefoneEmpresa: linhas.filter((l) => l.organization_phone_number_1).length,
    comContatoSocio: linhas.filter((l) => l.contact_1_phone_number_1 || l.contact_1_email_1).length,
    arquivo,
  };
}
